import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { icons } from '~/assets';
import Button from '~/components/button';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import Input from '~/components/newInput';
import Loading from '~/components/loading';
import {
  BankData as BankDataType,
  GeneralParametersData,
  SplitsModality,
} from '~/services/banknow/types';
import Endpoints from '~/services/endpoints';
import { ApiMethod, httpRequest } from '~/services/httpRequest';
import { cpfCnpjMask } from '~/utils/masks';
import BankData from '~/views/Teupag/parameters/components/BankData';
import GeneralData from '~/views/Teupag/parameters/components/GeneralData';
import MDRData from '~/views/Teupag/parameters/components/MDRData';

interface ApiSplitModality {
  splitPayment?: string;
  modality?: string;
  percentual?: number;
  percentualSplit?: number;
}

interface BusinessGroupProps {
  groupId?: string;
  businessGroupData?: {
    name: string;
    generalParameters: GeneralParametersData;
    homeAccount: Record<string, unknown>;
    splitsModalities: ApiSplitModality[];
  };
}

const INITIAL_GENERAL_PARAMETERS: GeneralParametersData = {
  mcc: 0,
  captureMethod: '',
  posQuantity: 0,
  monthlyCardTotalRevenue: 0,
  repassCondition: '',
};

const INITIAL_BANK_DATA: BankDataType = {
  accountHolder: { holderName: '', cpfCnpj: '' },
  bankName: '',
  agency: '',
  account: '',
  digit: '',
  bank: '',
  code: 0,
  type: '',
};

const INITIAL_SPLITS: SplitsModality[] = [
  { modality: 'A_VISTA', percentualSplit: 0 },
  { modality: 'PARCELADO_2X', percentualSplit: 0 },
  { modality: 'PARCELADO_3X', percentualSplit: 0 },
  { modality: 'PARCELADO_4X', percentualSplit: 0 },
  { modality: 'PARCELADO_5X', percentualSplit: 0 },
  { modality: 'PARCELADO_6X', percentualSplit: 0 },
  { modality: 'PARCELADO_7X', percentualSplit: 0 },
  { modality: 'PARCELADO_8X', percentualSplit: 0 },
  { modality: 'PARCELADO_9X', percentualSplit: 0 },
  { modality: 'PARCELADO_10X', percentualSplit: 0 },
  { modality: 'PARCELADO_11X', percentualSplit: 0 },
  { modality: 'PARCELADO_12X', percentualSplit: 0 },
];

const mapHomeAccountToBankData = (
  homeAccount: Record<string, unknown>,
): BankDataType => {
  if (!homeAccount) return INITIAL_BANK_DATA;
  const holder = homeAccount.accountHolder as
    | { cpfCnpj?: string; holderName?: string }
    | undefined;
  const rawCpfCnpj = holder?.cpfCnpj ?? (homeAccount.accountHolderCpfCnpj as string) ?? '';
  return {
    accountHolder: {
      holderName: holder?.holderName ?? (homeAccount.accountHolderName as string) ?? '',
      cpfCnpj: cpfCnpjMask(rawCpfCnpj),
    },
    bankName: (homeAccount.bankFullName as string) ?? '',
    agency: (homeAccount.branch as string) ?? '',
    account: (homeAccount.account as string) ?? '',
    digit: (homeAccount.digit as string) ?? '',
    bank: (homeAccount.bankIspb as string) ?? '',
    code: Number(homeAccount.bankCode) || 0,
    type: (homeAccount.accountType as string) ?? '',
  };
};

const mapApiSplitsToModalities = (
  apiSplits: ApiSplitModality[],
): SplitsModality[] =>
  apiSplits.map((split) => ({
    modality: split.splitPayment ?? split.modality ?? '',
    percentualSplit: split.percentual ?? split.percentualSplit ?? 0,
  }));

const buildHomeAccountPayload = (bankData: BankDataType) => ({
  bankFullName: bankData.bankName,
  bankCode: String(bankData.code),
  bankIspb: bankData.bank,
  branch: bankData.agency,
  account: bankData.account,
  digit: bankData.digit,
  accountType: 'CHECKING',
  accountHolderName: bankData.accountHolder?.holderName,
  accountHolderCpfCnpj:
    bankData.accountHolder?.cpfCnpj?.unmasked ??
    bankData.accountHolder?.cpfCnpj,
});

const BusinessGroupEstablishment = ({
  groupId,
  businessGroupData,
}: BusinessGroupProps) => {
  const isEditing = !!groupId;

  const [groupName, setGroupName] = useState(
    businessGroupData?.name ?? '',
  );
  const [generalParametersData, setGeneralParametersData] =
    useState<GeneralParametersData>(
      businessGroupData?.generalParameters ?? INITIAL_GENERAL_PARAMETERS,
    );
  const [bankData, setBankData] = useState<BankDataType>(
    businessGroupData?.homeAccount
      ? mapHomeAccountToBankData(businessGroupData.homeAccount)
      : INITIAL_BANK_DATA,
  );
  const [splitsModalities, setSplitsModalities] = useState<SplitsModality[]>(
    businessGroupData?.splitsModalities?.length
      ? mapApiSplitsToModalities(businessGroupData.splitsModalities)
      : INITIAL_SPLITS,
  );

  const [groupNameError, setGroupNameError] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogSuccess, setDialogSuccess] = useState(false);

  const isSubmitting = useRef(false);
  const generalDataRef = useRef<{ handleErrors: () => boolean }>();
  const bankDataRef = useRef<{ handleErrors: () => boolean }>();
  const history = useHistory();

  const handleSubmit = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      let hasErrors = false;

      if (!groupName.trim()) {
        setGroupNameError('Campo obrigatório');
        hasErrors = true;
      } else {
        setGroupNameError('');
      }

      const generalErrors = generalDataRef.current?.handleErrors();
      if (generalErrors) hasErrors = true;

      const bankErrors = bankDataRef.current?.handleErrors();
      if (bankErrors) hasErrors = true;

      if (hasErrors) return;

      const requestBody = {
        name: groupName,
        generalParameters: generalParametersData,
        homeAccount: buildHomeAccountPayload(bankData),
        splitsModalities: splitsModalities.map((split) => ({
          modality: split.modality,
          percentualSplit: split.percentualSplit,
        })),
      };

      setLoading(true);

      const url = isEditing
        ? Endpoints.companies.updateBusinessGroup.replace(
            'PARAM_GROUP_ID',
            groupId,
          )
        : Endpoints.companies.createBusinessGroup;

      const method = isEditing ? ApiMethod.PUT : ApiMethod.POST;

      const response = await httpRequest({
        url,
        method,
        body: requestBody,
      });

      if (response.status !== 200 && response.status !== 201) {
        setDialogMessage(response.data?.mensagem ?? 'Houve um erro');
        setDialogOpen(true);
        setLoading(false);
        return;
      }

      const successMessage = isEditing
        ? 'Grupo econômico atualizado com sucesso!'
        : 'Grupo econômico criado com sucesso!';

      setDialogMessage(successMessage);
      setDialogSuccess(true);
      setDialogOpen(true);
      setLoading(false);
    } catch (error) {
      setDialogMessage(error?.response?.data?.mensagem ?? 'Houve um erro');
      setDialogOpen(true);
      setLoading(false);
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <>
      <Loading status={loading} />
      <Dialog
        icon={icons.exclamation}
        open={dialogOpen}
        positiveLabel="Entendi"
        positiveAction={() => {
          setDialogOpen(false);
          if (dialogSuccess) {
            history.push('/teu-pag/estabelecimento', { listMode: 'businessGroups' });
          }
        }}
        message={dialogMessage}
      />
      <ContainerFlex>
        <ContainerFlex style={{ maxWidth: '49.5%' }}>
          <Input
            label="Nome do grupo econômico"
            name="groupName"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            errorMessage={groupNameError}
          />
        </ContainerFlex>

        <ContainerFlex marginTop={20}>
          <h3>Parâmetros Gerais</h3>
          <GeneralData
            ref={generalDataRef}
            defaultValues={generalParametersData}
            onChange={setGeneralParametersData}
          />
        </ContainerFlex>

        <ContainerFlex marginTop={20}>
          <h3>Dados Bancários</h3>
          <BankData
            ref={bankDataRef}
            defaultValues={bankData}
            onChange={setBankData}
          />
        </ContainerFlex>

        <ContainerFlex marginTop={20}>
          <h3>Taxa de administração (MDR)</h3>
          <MDRData
            defaultValues={splitsModalities}
            onChange={setSplitsModalities}
          />
        </ContainerFlex>

        <ContainerFlex row end marginTop={30} gap="30">
          <Button
            outlined
            fitContent
            onClick={() =>
              history.push('/teu-pag/estabelecimento', { listMode: 'businessGroups' })
            }
          >
            Voltar
          </Button>
          <Button fitContent onClick={handleSubmit} disabled={loading}>
            {isEditing ? 'Salvar' : 'Cadastrar'}
          </Button>
        </ContainerFlex>
      </ContainerFlex>
    </>
  );
};

export { BusinessGroupEstablishment };
